import React, { useState, useEffect } from 'react';
import axios from 'axios';
import EvidenceModal from './EvidenceModal';

function AssignmentManagement({ user, discipline = 'stem' }) {
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [assignmentEvidences, setAssignmentEvidences] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvidence, setEditingEvidence] = useState(null);
  const [editingObjectiveId, setEditingObjectiveId] = useState(null);

  useEffect(() => {
    loadData();
  }, [discipline]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, classesRes] = await Promise.all([
        axios.get('/outcomes/api/assignments', { params: { discipline }, withCredentials: true }),
        axios.get('/outcomes/api/classes', { params: { showAll: true }, withCredentials: true })
      ]);
      setAssignments(assignmentsRes.data);
      setClasses(classesRes.data);
      setError('');
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignmentEvidences = async (assignmentId) => {
    try {
      const response = await axios.get('/outcomes/api/evidence', {
        params: { assignmentId }
      });
      setAssignmentEvidences(prev => ({
        ...prev,
        [assignmentId]: response.data
      }));
    } catch (err) {
      console.error('Failed to load evidence for assignment:', err);
    }
  };

  const toggleAssignment = (assignmentId) => {
    if (expandedAssignment === assignmentId) {
      setExpandedAssignment(null);
    } else {
      setExpandedAssignment(assignmentId);
      if (!assignmentEvidences[assignmentId]) {
        loadAssignmentEvidences(assignmentId);
      }
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Delete this assignment and all associated evidence?')) {
      return;
    }

    try {
      await axios.delete(`/outcomes/api/assignments/${assignmentId}`);
      setAssignments(assignments.filter(a => a.id !== assignmentId));
      setExpandedAssignment(null);
    } catch (err) {
      console.error('Failed to delete assignment:', err);
      alert('Failed to delete assignment');
    }
  };

  const handleEditEvidence = (evidence) => {
    setEditingEvidence(evidence);
    setEditingObjectiveId(evidence.objective_id);
    setShowEditModal(true);
  };

  const handleDeleteEvidence = async (evidenceId, assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this evidence?')) {
      return;
    }

    try {
      await axios.delete(`/outcomes/api/evidence/${evidenceId}`, { withCredentials: true });
      // Refresh the evidence for this assignment
      if (assignmentId && assignmentEvidences[assignmentId]) {
        setAssignmentEvidences(prev => ({
          ...prev,
          [assignmentId]: prev[assignmentId].filter(e => e.id !== evidenceId)
        }));
      }
    } catch (err) {
      console.error('Failed to delete evidence:', err);
      alert('Failed to delete evidence');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingEvidence(null);
    setEditingObjectiveId(null);
  };

  const handleModalSave = (assignmentId) => {
    setShowEditModal(false);
    setEditingEvidence(null);
    setEditingObjectiveId(null);
    // Refresh the evidence for the assignment
    if (assignmentId && assignmentEvidences[assignmentId]) {
      loadAssignmentEvidences(assignmentId);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="assignment-management">
      <div className="assignment-header">
        <h2>Assignment Management</h2>
        <p className="subtitle">Manage evidences by assignment</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {assignments.length === 0 ? (
        <div className="empty-state">
          <p>No assignments yet. Create one by importing evidence from a JSON file.</p>
        </div>
      ) : (
        <div className="assignments-list">
          {assignments.map((assignment) => (
            <div key={assignment.id} className="assignment-card">
              <div className="assignment-card-header" onClick={() => toggleAssignment(assignment.id)}>
                <div className="assignment-info">
                  <h3>{assignment.name}</h3>
                  <p className="assignment-meta">
                    Class: <span>{assignment.class_id}</span> • 
                    Reach: <span>{assignment.reach}</span> • 
                    Evidence: <span>{assignment.evidence_count}</span>
                  </p>
                </div>
                <div className="assignment-actions">
                  <button
                    className="delete-assignment-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAssignment(assignment.id);
                    }}
                    title="Delete assignment"
                  >
                    🗑️
                  </button>
                  <span className={`expand-icon ${expandedAssignment === assignment.id ? 'expanded' : ''}`}>
                    ›
                  </span>
                </div>
              </div>

              {expandedAssignment === assignment.id && (
                <div className="assignment-details">
                  <div className="evidences-table">
                    {assignmentEvidences[assignment.id]?.length > 0 ? (
                      <table>
                        <thead>
                          <tr>
                            <th>Evidence</th>
                            <th>Level</th>
                            <th>Description</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignmentEvidences[assignment.id].map((evidence) => (
                            <tr key={evidence.id}>
                              <td>{evidence.title}</td>
                              <td>
                                <span className={`level-badge level-${evidence.level?.toLowerCase().replace(' ', '-')}`}>
                                  {evidence.level}
                                </span>
                              </td>
                              <td className="description-cell">{evidence.description?.substring(0, 100)}...</td>
                              <td className="date-cell">{new Date(evidence.created_at).toLocaleDateString()}</td>
                              <td className="actions-cell">
                                <button
                                  className="evidence-action-btn edit-btn"
                                  onClick={() => handleEditEvidence(evidence)}
                                  title="Edit evidence"
                                >
                                  ✎
                                </button>
                                <button
                                  className="evidence-action-btn delete-btn"
                                  onClick={() => handleDeleteEvidence(evidence.id, assignment.id)}
                                  title="Delete evidence"
                                >
                                  🗑
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="no-evidence">No evidence in this assignment</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showEditModal && editingEvidence && (
        <EvidenceModal
          objectiveId={editingObjectiveId}
          objectiveText=""
          classes={classes}
          discipline={discipline}
          evidence={editingEvidence}
          onClose={handleModalClose}
          onSave={() => handleModalSave(expandedAssignment)}
        />
      )}
    </div>
  );
}

export default AssignmentManagement;
